import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { decodeWorkflowContent } from "../../../api/content/authoring/processContentDecoders";
import type { ItemChange } from "../../../api/content/reviewDecoders";
import type { JsonObject } from "../../../api/http/jsonDecoding";
import { useItemVersionPair } from "../../items/changes/useItemVersionPair";
import FieldChangeList from "../../items/changes/FieldChangeList";
import { fieldDiff } from "../../../shared/diff/fieldDiff";
import WorkflowGraphDiff from "./WorkflowGraphDiff";
import LoadStatus from "../../../ui/feedback/LoadStatus";

/**
 * A process definition's changes as `path: old → new`, pinned references by name and short
 * version; a Workflow also as a small graph diff above it.
 */
export default function DefinitionDiff({ item }: { item: ItemChange }) {
  const { t } = useTranslation();
  const { state, reload } = useItemVersionPair(item);
  const versions = state.status === "loaded" ? state.value : null;
  const before = versions?.before?.definition ?? null;
  const after = versions?.after?.definition ?? null;
  const changes = useMemo(() => fieldDiff(before ?? undefined, after ?? undefined), [before, after]);
  const workflows = useMemo(() => (item.kind === "workflow" ? workflowPair(before, after) : null), [item.kind, before, after]);
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("reviews.item.loadingDefinition")} onRetry={reload} compact />;
  return (
    <div className="space-y-2">
      {workflows === null ? null : (
        <div>
          <h4 className="app-subsection-title text-sm">{t("reviews.item.graphDiff")}</h4>
          <WorkflowGraphDiff before={workflows.before} after={workflows.after} />
        </div>
      )}
      <div>
        <h4 className="app-subsection-title text-sm">{t("reviews.item.fieldDiff")}</h4>
        {/* An added or removed item is shown whole, as one change, by the item link instead. */}
        {before === null || after === null ? <p className="app-text-muted text-xs">{t(before === null ? "reviews.item.wholeAdded" : "reviews.item.wholeRemoved")}</p> : <FieldChangeList changes={changes} />}
      </div>
    </div>
  );
}

/** Both versions as Workflows; an added Workflow diffs against an empty one. */
function workflowPair(before: JsonObject | null, after: JsonObject | null) {
  if (after === null) {
    return null;
  }
  const next = decodeWorkflowContent(after);
  return { before: before === null ? { ...next, steps: [], exports: {} } : decodeWorkflowContent(before), after: next };
}
